import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Heart, Share2 } from 'lucide-react';

// v2.0 - Debugging play button
const SharedSong = ({ playSong, songs, toggleLike, likedSongs, user }) => {
  const { songId } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);

  console.log('🔍 SharedSong v2.0 rendered, playSong is:', typeof playSong);

  useEffect(() => {
    fetchSong();
  }, [songId]);

  const fetchSong = async () => {
    try {
      // Try to find in existing songs first
      const existingSong = songs.find(s => s.id === songId);
      if (existingSong) {
        setSong(existingSong);
        setLoading(false);
        return;
      }

      // Otherwise fetch from API
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/${songId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSong(data);
      }
    } catch (error) {
      console.error('Failed to fetch song:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/song/${song.id}`;
    navigator.clipboard.writeText(shareUrl);
    alert('Song link copied to clipboard!');
  };

  console.log('🔍 SharedSong render state:', { loading, song: song?.title, hasSong: !!song });

  if (loading) {
    return (
      <div className="page-content">
        <div className="loading-state">Loading song...</div>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="page-content">
        <div className="error-state">
          <h2>Song not found</h2>
          <p>This song may have been removed or the link is invalid.</p>
          <button onClick={() => navigate('/')} className="back-home-btn">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="shared-song-container">
        <div 
          className="shared-song-hero"
          style={{
            background: 'linear-gradient(180deg, rgba(157, 78, 221, 0.3) 0%, transparent 100%)',
            borderRadius: '16px',
            padding: '32px 40px',
            marginBottom: '32px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '32px'
          }}
        >
          <div 
            className="shared-song-artwork"
            style={{
              position: 'relative',
              width: '120px',
              height: '120px',
              flexShrink: 0,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <img 
              src={song.albumArt || song.album_art_url} 
              alt={song.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <button 
              className="play-overlay-button"
              onClick={(e) => {
                e.stopPropagation();
                playSong(song, [song]);
              }}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(157, 78, 221, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <Play size={28} fill="white" />
            </button>
          </div>
          <div className="shared-song-info" style={{ flex: 1, color: '#fff' }}>
            <div 
              className="song-type"
              style={{
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                opacity: 0.8,
                marginBottom: '8px'
              }}
            >
              SONG
            </div>
            <h1 
              className="shared-song-title"
              style={{
                fontSize: '48px',
                fontWeight: '900',
                color: '#fff',
                margin: '0 0 12px 0',
                lineHeight: '1.1',
                textShadow: '0 4px 12px rgba(0,0,0,0.5)'
              }}
            >
              {song.title}
            </h1>
            <p 
              className="shared-song-artist"
              style={{
                fontSize: '20px',
                color: '#fff',
                margin: '0 0 12px 0',
                opacity: 0.9
              }}
            >
              {song.artist}
            </p>
            <div 
              className="shared-song-stats"
              style={{
                fontSize: '14px',
                opacity: 0.8,
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                color: '#fff'
              }}
            >
              <span>{song.album}</span>
              <span>•</span>
              <span>{Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}</span>
            </div>
          </div>
        </div>

        <div 
          className="shared-song-actions"
          style={{
            display: 'flex',
            gap: '16px',
            padding: '0 40px 24px',
            alignItems: 'center'
          }}
        >
          <button 
            className="action-btn primary"
            onClick={(e) => {
              e.stopPropagation();
              console.log('🎵 CLICK DETECTED! Song:', song?.title);
              console.log('🎵 Song data:', JSON.stringify(song, null, 2));
              if (playSong && song) {
                playSong(song, [song]);
              } else {
                console.error('❌ Missing playSong or song:', { playSong: !!playSong, song: !!song });
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 40px',
              borderRadius: '24px',
              border: 'none',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              background: '#9D4EDD',
              color: '#fff'
            }}
          >
            <Play size={20} fill="white" />
            Play
          </button>
          <button 
            className={`action-btn ${likedSongs?.has(song.id) ? 'liked' : ''}`}
            onClick={() => toggleLike(song.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#fff'
            }}
          >
            <Heart size={20} fill={likedSongs?.has(song.id) ? '#9D4EDD' : 'none'} />
          </button>
          <button 
            className="action-btn"
            onClick={handleShare}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#fff'
            }}
          >
            <Share2 size={20} />
          </button>
        </div>

        {!user && (
          <div 
            className="signup-prompt"
            style={{
              background: 'linear-gradient(135deg, rgba(157, 78, 221, 0.2) 0%, rgba(255, 208, 10, 0.1) 100%)',
              border: '2px solid rgba(157, 78, 221, 0.4)',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center',
              margin: '0 48px'
            }}
          >
            <h3 style={{ fontSize: '24px', marginBottom: '12px', color: '#fff' }}>
              Want to save this song?
            </h3>
            <p style={{ color: '#b3b3b3', marginBottom: '24px' }}>
              Create an account to save songs, create playlists, and more!
            </p>
            <button 
              onClick={() => navigate('/login')} 
              className="signup-btn"
              style={{
                background: '#9D4EDD',
                color: '#fff',
                border: 'none',
                padding: '16px 40px',
                borderRadius: '24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Sign Up / Log In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedSong;
